import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, XCircle, Volume2, VolumeX, AlertTriangle, Lightbulb, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cleanTextForTTS = (text) => {
  if (!text) return '';
  let cleaned = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1');
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  cleaned = cleaned.replace(/<[^>]+>/g, '');
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  return cleaned;
};

const parseContent = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold text-violet-400">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Female Avatar SVG - Fixed, always visible
const FemaleAvatar = ({ isSpeaking }) => {
  const [mouthPhase, setMouthPhase] = useState(0);
  const [blinkState, setBlinkState] = useState(false);
  
  useEffect(() => {
    if (!isSpeaking) {
      setMouthPhase(0);
      return;
    }
    const interval = setInterval(() => {
      setMouthPhase(prev => (prev + 1) % 4);
    }, 100);
    return () => clearInterval(interval);
  }, [isSpeaking]);
  
  useEffect(() => {
    const blink = () => {
      setBlinkState(true);
      setTimeout(() => setBlinkState(false), 150);
    };
    const interval = setInterval(blink, 3500 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, []);
  
  const mouthShapes = [
    { rx: 6, ry: 2 },
    { rx: 8, ry: 5 },
    { rx: 10, ry: 7 },
    { rx: 7, ry: 4 },
  ];
  
  const currentMouth = isSpeaking ? mouthShapes[mouthPhase] : mouthShapes[0];
  
  return (
    <div className="relative w-24 h-24 mx-auto flex-shrink-0">
      {isSpeaking && (
        <div className="absolute inset-0 rounded-full bg-pink-400/30 blur-xl animate-pulse" />
      )}
      
      <svg viewBox="0 0 200 200" className="w-full h-full relative z-10 drop-shadow-xl">
        <defs>
          <linearGradient id="bgGradF" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="50%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="skinGradF" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fef3e2" />
            <stop offset="50%" stopColor="#fde8d0" />
            <stop offset="100%" stopColor="#f5d5c0" />
          </linearGradient>
          <linearGradient id="hairGradF" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#5c3d2e" />
            <stop offset="50%" stopColor="#3d2518" />
            <stop offset="100%" stopColor="#2a1810" />
          </linearGradient>
          <linearGradient id="lipGradF" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e11d48" />
            <stop offset="100%" stopColor="#be123c" />
          </linearGradient>
          <radialGradient id="irisGradF" cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#4c1d95" />
          </radialGradient>
        </defs>
        
        {isSpeaking && (
          <circle cx="100" cy="100" r="95" fill="none" stroke="url(#bgGradF)" strokeWidth="2" opacity="0.5">
            <animate attributeName="r" values="90;98;90" dur="1s" repeatCount="indefinite" />
          </circle>
        )}
        
        <circle cx="100" cy="100" r="88" fill="url(#bgGradF)" />
        <ellipse cx="100" cy="120" rx="70" ry="65" fill="url(#hairGradF)" />
        <ellipse cx="100" cy="100" rx="50" ry="55" fill="url(#skinGradF)" />
        <ellipse cx="100" cy="55" rx="50" ry="30" fill="url(#hairGradF)" />
        <ellipse cx="45" cy="95" rx="18" ry="50" fill="url(#hairGradF)" />
        <ellipse cx="155" cy="95" rx="18" ry="50" fill="url(#hairGradF)" />
        
        <ellipse cx="77" cy="95" rx="12" ry={blinkState ? 1 : 14} fill="white" />
        {!blinkState && (
          <>
            <circle cx="77" cy="95" r="8" fill="url(#irisGradF)" />
            <circle cx="77" cy="95" r="4" fill="#1e1b4b" />
            <circle cx="74" cy="92" r="2.5" fill="white" />
          </>
        )}
        
        <ellipse cx="123" cy="95" rx="12" ry={blinkState ? 1 : 14} fill="white" />
        {!blinkState && (
          <>
            <circle cx="123" cy="95" r="8" fill="url(#irisGradF)" />
            <circle cx="123" cy="95" r="4" fill="#1e1b4b" />
            <circle cx="120" cy="92" r="2.5" fill="white" />
          </>
        )}
        
        {isSpeaking ? (
          <ellipse cx="100" cy="132" rx={currentMouth.rx} ry={currentMouth.ry} fill="url(#lipGradF)" />
        ) : (
          <path d="M 88 128 Q 100 138 112 128" fill="url(#lipGradF)" />
        )}
      </svg>
    </div>
  );
};

const StepTypeBadge = ({ type }) => {
  if (type === 'warning') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium">
        <AlertTriangle className="w-4 h-4" />
        Attenzione
      </span>
    );
  }
  if (type === 'tip') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium">
        <Lightbulb className="w-4 h-4" />
        Suggerimento
      </span>
    );
  }
  return null;
};

export default function PublicGuidePage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  
  // Completion confirmation state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [completionConfirmed, setCompletionConfirmed] = useState(false);
  
  const audioRef = useRef(null);

  // Fetch guide data
  useEffect(() => {
    const fetchGuide = async () => {
      if (!token) {
        setError("Link non valido");
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API}/public/guide/${slug}?t=${token}`);
        if (!res.ok) {
          setError("Link non valido o guida non pubblicata");
          setLoading(false);
          return;
        }
        setData(await res.json());
      } catch (e) {
        setError("Errore nel caricamento della guida");
      } finally {
        setLoading(false);
      }
    };
    fetchGuide();
  }, [slug, token]);

  // Speak with TTS (ElevenLabs Bella voice)
  const speakWithTTS = useCallback(async (text) => {
    if (isMuted || !text) return;
    
    setIsLoadingAudio(true);
    
    try {
      const res = await fetch(`${API}/public/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: cleanTextForTTS(text),
          voice_id: 'Ilze0lNihfxGj7pq9g7s' // Default Italian voice
        })
      });
      
      if (!res.ok) {
        setIsLoadingAudio(false);
        return;
      }
      
      const result = await res.json();
      
      if (result.audio_url) {
        if (!audioRef.current) {
          audioRef.current = new Audio();
        }
        
        audioRef.current.src = result.audio_url;
        audioRef.current.onplay = () => {
          setIsSpeaking(true);
          setIsLoadingAudio(false);
        };
        audioRef.current.onended = () => setIsSpeaking(false);
        audioRef.current.onerror = () => {
          setIsSpeaking(false);
          setIsLoadingAudio(false);
        };
        
        await audioRef.current.play();
      } else {
        setIsLoadingAudio(false);
      }
    } catch (e) {
      console.error('TTS error:', e);
      setIsLoadingAudio(false);
    }
  }, [isMuted]);

  // Handle OK button click
  const handleOkClick = useCallback(async () => {
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
    
    // Determine next step
    let nextStep;
    let textToSpeak;
    
    if (currentStep === -1) {
      nextStep = 0;
    } else if (currentStep < data.steps.length - 1) {
      nextStep = currentStep + 1;
    } else {
      // Complete
      setIsComplete(true);
      speakWithTTS("Perfetto! Hai completato tutti i passi. Ottimo lavoro!");
      return;
    }
    
    setCurrentStep(nextStep);

    // Send progress update to backend (fire and forget)
    if (data.guideId) {
      fetch(`${API}/public/guide/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guide_id: data.guideId,
          current_step: nextStep + 1,
          total_steps: data.steps.length
        })
      }).catch(() => {});
    }
    
    // Get text for new step and speak
    const step = data.steps[nextStep];
    textToSpeak = `Passo ${step.order}. ${step.title}. ${step.content || ''}`;
    if (step.type === 'warning') textToSpeak = `Attenzione! ${textToSpeak}`;
    else if (step.type === 'tip') textToSpeak = `Un suggerimento: ${textToSpeak}`;
    
    speakWithTTS(textToSpeak);
  }, [currentStep, data, speakWithTTS]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Confirm completion function
  const confirmCompletion = async () => {
    setIsConfirming(true);
    try {
      // Get customer info from URL or localStorage
      const customerName = searchParams.get('name') || localStorage.getItem('guideName') || 'Cliente';
      const customerPhone = searchParams.get('phone') || localStorage.getItem('guidePhone') || '';
      
      const res = await fetch(`${API}/public/guide-completed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guide_slug: slug,
          guide_token: token,
          customer_name: customerName,
          customer_phone: customerPhone
        })
      });
      
      if (res.ok) {
        setCompletionConfirmed(true);
        setShowConfirmDialog(false);
      } else {
        alert('Errore nella conferma. Riprova.');
      }
    } catch (err) {
      console.error('Error confirming completion:', err);
      alert('Errore di connessione. Riprova.');
    } finally {
      setIsConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Errore</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <FemaleAvatar isSpeaking={isSpeaking} />
          <div className="mt-6 text-center">
            <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-emerald-600">Completato! 🎉</h1>
            <p className="text-lg text-gray-600 mt-2">Tutti i {data.steps.length} passi completati</p>
            
            {/* Confirmation Button */}
            {!completionConfirmed ? (
              <div className="mt-8">
                <Button
                  onClick={() => setShowConfirmDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 rounded-xl shadow-lg"
                  disabled={isConfirming}
                >
                  {isConfirming ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  ✅ Conferma completamento
                </Button>
                <p className="text-sm text-gray-500 mt-3">
                  Clicca per confermare l'annullamento scontrino
                </p>
              </div>
            ) : (
              <div className="mt-8 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-700 font-medium">Conferma inviata!</p>
                <p className="text-sm text-gray-600 mt-1">L'helpdesk è stato notificato</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Conferma completamento</h2>
              <p className="text-gray-600 text-center mb-6">
                Confermi di aver completato correttamente l'annullamento dello scontrino?
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 border-gray-300 text-gray-700"
                  disabled={isConfirming}
                >
                  Annulla
                </Button>
                <Button
                  onClick={confirmCompletion}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={isConfirming}
                >
                  {isConfirming ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  OK, Confermo
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Footer with Logo */}
        <footer className="flex-shrink-0 pb-16 border-t border-white/10">
          <div className="bg-white py-6 px-6">
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_avatar-guides/artifacts/7j9z0dtd_6545acf1-4691-42b9-b453-0b887e998200.pdf.png" 
                alt="SDG - Sistemi Digitali Group"
                className="h-28 object-contain"
              />
            </div>
          </div>
        </footer>
      </div>
    );
  }

  const step = currentStep >= 0 ? data.steps[currentStep] : null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header with Avatar */}
      <header className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          {/* Fixed Avatar in header */}
          <FemaleAvatar isSpeaking={isSpeaking} />
          
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate">{data.modelName}</p>
            <h1 className="text-base font-semibold text-gray-900 truncate">{data.guide.title}</h1>
            {/* Audio indicator */}
            {(isLoadingAudio || isSpeaking) && (
              <div className="flex items-center gap-1 mt-1">
                {isLoadingAudio ? (
                  <span className="text-xs text-primary flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Carico...
                  </span>
                ) : (
                  <div className="flex items-center gap-0.5">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="w-1 bg-primary rounded-full animate-pulse" 
                           style={{ height: `${6 + Math.random() * 8}px`, animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setIsMuted(!isMuted)} className="p-2 text-gray-500 hover:text-gray-900">
              {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
            </button>
            <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg">
              {currentStep === -1 ? '0' : currentStep + 1}/{data.steps.length}
            </span>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="flex-shrink-0 h-1.5 bg-gray-100">
        <div 
          className="h-full bg-gradient-to-r from-primary to-violet-500 transition-all duration-300"
          style={{ width: `${((currentStep + 2) / (data.steps.length + 1)) * 100}%` }}
        />
      </div>

      {/* TOP Buttons */}
      <div className="flex-shrink-0 p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex gap-3">
          {/* Back Button - only show after intro */}
          {currentStep >= 0 && (
            <Button
              onClick={() => setCurrentStep(prev => prev - 1)}
              disabled={isLoadingAudio}
              variant="outline"
              className="h-14 px-4 text-lg font-bold rounded-2xl border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              data-testid="back-button"
            >
              <ChevronLeft className="w-6 h-6" />
              <span className="hidden sm:inline ml-1">Indietro</span>
            </Button>
          )}
          
          {/* Forward Button */}
          <Button
            onClick={handleOkClick}
            disabled={isLoadingAudio}
            className="flex-1 h-14 text-lg font-bold rounded-2xl bg-primary hover:bg-primary/90 disabled:opacity-50 shadow-lg text-white"
            data-testid="ok-button"
          >
            {isLoadingAudio ? (
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
            ) : (
              <CheckCircle className="w-6 h-6 mr-2" />
            )}
            {currentStep === -1 ? "OK, Iniziamo!" : "OK, Avanti"}
            <ChevronRight className="w-6 h-6 ml-1" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-4">
          {currentStep === -1 ? (
            /* Intro screen */
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Ciao! 👋</h2>
              <p className="text-lg text-gray-600">Sono qui per guidarti passo passo</p>
              <p className="text-base text-gray-500 mt-4">Premi il pulsante in alto per iniziare</p>
            </div>
          ) : step && (
            /* Step content */
            <div className="space-y-4">
              {/* Step header */}
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center text-xl font-bold shadow-lg">
                  {step.order}
                </span>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">{step.title}</h2>
                  {step.type !== 'normal' && (
                    <div className="mt-1">
                      <StepTypeBadge type={step.type} />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Step content text - BIGGER */}
              {step.content && (
                <p className="text-lg text-gray-700 leading-relaxed">
                  {parseContent(step.content)}
                </p>
              )}
              
              {/* Images - FULL WIDTH, BIG */}
              {step.images?.length > 0 && (
                <div className="space-y-3 mt-4">
                  {step.images.map((img, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                      <img 
                        src={img} 
                        alt={`Immagine ${i + 1}`} 
                        className="w-full h-auto object-contain bg-gray-50"
                        style={{ maxHeight: '50vh' }}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {/* Video if present - YouTube/Vimeo embed */}
              {step.video_type === 'url' && step.video_url && (
                <div className="mt-4 rounded-xl overflow-hidden border border-gray-200">
                  <div className="aspect-video bg-gray-100">
                    <iframe
                      src={step.video_url.replace('watch?v=', 'embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title="Video"
                    />
                  </div>
                  {step.video_caption && (
                    <p className="text-sm text-gray-600 p-3 bg-gray-50">{step.video_caption}</p>
                  )}
                </div>
              )}
              
              {/* Video if present - Uploaded MP4 file (autoplay) */}
              {step.video_type === 'upload' && step.video_file_url && (
                <div className="mt-4 rounded-xl overflow-hidden border border-gray-200">
                  <video 
                    src={step.video_file_url} 
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full bg-gray-100"
                    style={{ maxHeight: '50vh' }}
                  />
                  {step.video_caption && (
                    <p className="text-sm text-gray-600 p-3 bg-gray-50">{step.video_caption}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Footer with Logo */}
      <footer className="flex-shrink-0 pb-16 border-t border-gray-200">
        <div className="bg-white py-6 px-6">
          <div className="flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_avatar-guides/artifacts/7j9z0dtd_6545acf1-4691-42b9-b453-0b887e998200.pdf.png" 
              alt="SDG - Sistemi Digitali Group"
              className="h-28 object-contain"
            />
          </div>
        </div>
      </footer>
    </div>
  );
}
